import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

export default function Landing() {
  const navigate = useNavigate()
  const [referralCode, setReferralCode] = useState('')
  const [referralLoading, setReferralLoading] = useState(false)
  const [referralError, setReferralError] = useState('')
  const [referralSuccess, setReferralSuccess] = useState(false)

  const handleRedeemReferral = async () => {
    if (!referralCode.trim()) {
      setReferralError('Please enter a referral code')
      return
    }

    setReferralLoading(true)
    setReferralError('')

    try {
      const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'
      const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL?.replace(/\/api(\/v1)?$/, '') || 'https://api.cognavia.ai')

      const response = await fetch(`${baseUrl}/api/auth/redeem-referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: referralCode.trim().toUpperCase() })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid or expired referral code')
      }

      if (data.success) {
        setReferralSuccess(true)

        // Store scraping job ID for progress tracking after login
        if (data.scraping_job_id) {
          sessionStorage.setItem('scraping_job_id', data.scraping_job_id)
        }

        // Redirect to login page with subscribe plan so they land on /subscribe after login
        sessionStorage.setItem('pending_plan', 'growth_6month')
        setTimeout(() => {
          window.location.assign('/login?plan=growth_6month')
        }, 2000)
      }
    } catch (err) {
      setReferralError(err.message)
    } finally {
      setReferralLoading(false)
    }
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-purple-600">CognaAssist</div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-purple-600">Features</a>
              <a href="#pricing" className="text-gray-700 hover:text-purple-600">Pricing</a>
              <a href="#docs" className="text-gray-700 hover:text-purple-600">Docs</a>
              <a href="#redeem" className="text-purple-600 hover:text-purple-700 font-medium">Have a Code?</a>
              <Link to="/login" className="text-gray-700 hover:text-purple-600">Sign In</Link>
              <Link 
                to="/register"
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold leading-tight mb-6">
                Turn website visitors into customers with AI-powered conversations
              </h1>
              <p className="text-xl mb-8 text-purple-100">
                Add an intelligent chatbot to your website with just 2 lines of code. 
                99%+ uptime, instant setup, and powered by advanced RAG technology for accurate, context-aware responses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/register"
                  className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center"
                >
                  Start for Free
                </Link>
                <a 
                  href="#features"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors text-center"
                >
                  See Demo
                </a>
              </div>
              <div className="mt-8 text-purple-100">
                <div className="flex items-center space-x-6">
                  <span className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>99%+ Uptime</span>
                  <span className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>30-second Setup</span>
                  <span className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>No Dependencies</span>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="bg-gray-900 rounded-lg p-4 text-sm font-mono text-gray-300">
                  <div className="text-gray-400 mb-2">&lt;!-- Add to your website --&gt;</div>
                  <div className="text-green-400">&lt;script src="https://cdn.cognavia.com/widget.js"&gt;&lt;/script&gt;</div>
                  <div className="text-blue-400">&lt;script&gt;CognaAssist.init(&#123;</div>
                  <div className="text-yellow-400 ml-4">apiKey: 'your-api-key'</div>
                  <div className="text-blue-400">&#125;);&lt;/script&gt;</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why choose CognaAssist?</h2>
            <p className="text-xl text-gray-600">Everything you need to provide intelligent customer support</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Instant Setup</h3>
              <p className="text-gray-600 mb-4">Add our widget to your website with just 2 lines of code. No complex integrations or technical expertise required.</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• 30-second installation</li>
                <li>• No external dependencies</li>
                <li>• Works on any website</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Smart RAG Technology</h3>
              <p className="text-gray-600 mb-4">Our AI automatically learns from your website content to provide accurate, context-aware responses to customer questions.</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Automatic content crawling</li>
                <li>• Vector-based search</li>
                <li>• OpenAI-powered responses</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Enterprise Ready</h3>
              <p className="text-gray-600 mb-4">Multi-tenant architecture with complete data isolation, 99%+ uptime, and enterprise-grade security.</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Multi-tenant isolation</li>
                <li>• 99%+ uptime guarantee</li>
                <li>• SOC 2 compliant</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get started in minutes</h2>
            <p className="text-xl text-gray-600">Three simple steps to intelligent customer support</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">1</div>
              <h3 className="text-xl font-semibold mb-4">Sign Up & Register Your Website</h3>
              <p className="text-gray-600">Create your account and tell us about your website. Our AI will automatically crawl and learn your content.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">2</div>
              <h3 className="text-xl font-semibold mb-4">Add Widget Code</h3>
              <p className="text-gray-600">Copy and paste 2 lines of code to your website. The chatbot will appear automatically and start learning.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">3</div>
              <h3 className="text-xl font-semibold mb-4">Start Converting Visitors</h3>
              <p className="text-gray-600">Your AI assistant is ready! Watch as it answers customer questions and helps convert visitors into customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Redeem Referral Code */}
      <section id="redeem" className="py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Have a Referral Code?</h2>
            <p className="text-xl text-gray-600">Enter your referral code below to activate your account</p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
            {referralSuccess ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Activated!</h3>
                <p className="text-gray-600 mb-4">Redirecting you to login...</p>
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralError(''); }}
                    placeholder="REF-XXXXXX"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-mono tracking-wider text-center"
                    maxLength={10}
                    disabled={referralLoading}
                  />
                  <button
                    onClick={handleRedeemReferral}
                    disabled={referralLoading || !referralCode.trim()}
                    className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {referralLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      'Redeem Code'
                    )}
                  </button>
                </div>

                {referralError && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">{referralError}</p>
                  </div>
                )}

                <p className="mt-4 text-sm text-gray-500 text-center">
                  Received a referral code via email? Enter it above to activate your CognaAssist account.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">CognaAssist Pricing</h2>
            <p className="text-xl text-gray-600">Turn Conversations Into Customers</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {/* 7-Day Revenue Trial */}
            <div className="bg-white p-8 rounded-xl shadow-sm border flex flex-col">
              <h3 className="text-xl font-semibold mb-4">7-Day Revenue Trial</h3>
              <div className="mb-3">
                <span className="text-4xl font-bold">{'\u20B9'}0</span>
                <span className="text-gray-600"> for 7 days</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Experience real-time AI chat</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Engage your website visitors instantly</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Limited conversations</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-red-400 rounded-full mr-3 flex-shrink-0"></span>No email integration</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-red-400 rounded-full mr-3 flex-shrink-0"></span>No CRM integration</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-red-400 rounded-full mr-3 flex-shrink-0"></span>No automation workflows</li>
              </ul>
              <p className="text-xs text-gray-500 mb-3">No setup required. No commitment.</p>
              <p className="text-xs text-gray-500 mb-4">Upgrade within 7 days and get 50% off onboarding fee</p>
              <Link
                to="/register"
                className="block w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors text-center font-semibold mt-auto"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Starter Plan (Monthly) */}
            <div className="bg-white p-8 rounded-xl shadow-sm border flex flex-col">
              <h3 className="text-xl font-semibold mb-4">Starter Plan</h3>
              <div className="mb-1">
                <span className="text-4xl font-bold">{'\u20B9'}2,999</span>
                <span className="text-gray-600"> / month</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">Onboarding &amp; AI Training: <span className="font-semibold text-gray-700">{'\u20B9'}2,999</span> (one-time)</p>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Full chat capabilities</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Email integration</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>CRM integration</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Guided onboarding</li>
              </ul>
              <p className="text-xs text-gray-500 mb-4">Best for evaluating with real business use cases</p>
              <button
                onClick={() => {
                  const token = localStorage.getItem('auth_token')
                  if (token) {
                    navigate('/subscribe?plan=starter_monthly')
                  } else {
                    sessionStorage.setItem('pending_plan', 'starter_monthly')
                    navigate('/register?plan=starter_monthly')
                  }
                }}
                className="block w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors text-center font-semibold mt-auto"
              >
                Subscribe Now
              </button>
            </div>

            {/* Growth Plan (6 Months) - Most Popular */}
            <div className="bg-white p-8 rounded-xl shadow-sm border-2 border-purple-600 relative flex flex-col">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm">Most Popular</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Growth Plan <span className="text-sm text-gray-500">(6 Months)</span></h3>
              <div className="mb-1">
                <span className="text-4xl font-bold">{'\u20B9'}2,499</span>
                <span className="text-gray-600"> / month</span>
              </div>
              <p className="text-sm text-gray-500 mb-1">{'\u20B9'}14,994 billed upfront</p>
              <p className="text-sm text-gray-500 mb-4">Onboarding: <span className="font-semibold text-gray-700">{'\u20B9'}999</span> (limited-time offer)</p>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Everything in Starter</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Priority onboarding</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Faster support</li>
              </ul>
              <p className="text-xs text-gray-500 mb-4">Balanced commitment with strong savings and real ROI potential</p>
              <button
                onClick={() => {
                  const token = localStorage.getItem('auth_token')
                  if (token) {
                    navigate('/subscribe?plan=growth_6month')
                  } else {
                    sessionStorage.setItem('pending_plan', 'growth_6month')
                    navigate('/register?plan=growth_6month')
                  }
                }}
                className="block w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors text-center font-semibold mt-auto"
              >
                Subscribe Now
              </button>
            </div>

            {/* Pro Plan (Annual) - Best Value */}
            <div className="bg-white p-8 rounded-xl shadow-sm border-2 border-green-500 relative flex flex-col">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-500 text-white px-4 py-1 rounded-full text-sm">Best Value</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Pro Plan <span className="text-sm text-gray-500">(Annual)</span></h3>
              <div className="mb-1">
                <span className="text-4xl font-bold">{'\u20B9'}1,999</span>
                <span className="text-gray-600"> / month</span>
              </div>
              <p className="text-sm text-gray-500 mb-1">{'\u20B9'}23,988 billed annually</p>
              <p className="text-sm text-green-600 font-semibold mb-4">Onboarding: FREE</p>
              <ul className="space-y-3 mb-6 flex-1">
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Everything in Growth</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Dedicated onboarding support</li>
                <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-3 flex-shrink-0"></span>Priority assistance</li>
              </ul>
              <p className="text-xs text-gray-500 mb-4">Maximum savings. Built for businesses serious about growth</p>
              <button
                onClick={() => {
                  const token = localStorage.getItem('auth_token')
                  if (token) {
                    navigate('/subscribe?plan=pro_annual')
                  } else {
                    sessionStorage.setItem('pending_plan', 'pro_annual')
                    navigate('/register?plan=pro_annual')
                  }
                }}
                className="block w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors text-center font-semibold mt-auto"
              >
                Subscribe Now
              </button>
            </div>
          </div>

          {/* Why CognaAssist */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Why CognaAssist</h3>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <p className="text-gray-700">Built for real business outcomes&mdash;not just conversations</p>
              </div>
              <div className="text-center">
                <p className="text-gray-700">Structured onboarding ensures faster results</p>
              </div>
              <div className="text-center">
                <p className="text-gray-700">Flexible plans to match your growth stage</p>
              </div>
            </div>
          </div>

          {/* ROI Promise */}
          <div className="mt-12 bg-purple-50 rounded-xl p-8 text-center max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-purple-900 mb-3">ROI Promise</h3>
            <p className="text-purple-700 text-lg">
              If CognaAssist helps you convert just 1&ndash;2 additional customers per month, it pays for itself.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-purple-600 to-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to turn visitors into customers?</h2>
          <p className="text-xl mb-8 text-purple-100">Join thousands of businesses using CognaAssist to provide intelligent customer support.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </Link>
            <a 
              href="#features"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Schedule Demo
            </a>
          </div>
          <p className="mt-6 text-purple-200 text-sm">No credit card required • 7-day free trial • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-purple-400 mb-4">CognaAssist</div>
              <p className="text-gray-400">AI-powered chatbots for modern businesses.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#docs" className="hover:text-white">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/contact" className="hover:text-white">Contact</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/help" className="hover:text-white">Help Center</a></li>
                <li><a href="/status" className="hover:text-white">Status</a></li>
                <li><Link to="/login" className="hover:text-white">Admin Login</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CognaAssist. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}